import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/desenho.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const AmbienteDesenhoManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_ambiente: '',
    file: null,
    id_tipo_representacao: '',
    principal: true
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [ambientes, setAmbientes] = useState([]);
  const [ambienteSearch, setAmbienteSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolaId, setEscolaId] = useState('');
  const [pavimentos, setPavimentos] = useState([]);
  const [pavimentoSearch, setPavimentoSearch] = useState('');
  const [pavimentoId, setPavimentoId] = useState('');
  const [tiposRepresentacao, setTiposRepresentacao] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    const s = escolaSearch.toLowerCase();
    return escolas.filter(e =>
      e.nome_escola?.toLowerCase().includes(s) ||
      e.codigo_inep?.toLowerCase().includes(s)
    );
  }, [escolas, escolaSearch]);

  const pavimentosFiltrados = useMemo(() => {
    const porEscola = escolaId ? pavimentos.filter(p => p.id_escola === escolaId) : [];
    if (!pavimentoSearch) return porEscola;
    const s = pavimentoSearch.toLowerCase();
    return porEscola.filter(p =>
      String(p.id_pavimento).toLowerCase().includes(s) ||
      p.descr_pavimento?.toLowerCase().includes(s)
    );
  }, [pavimentos, pavimentoSearch, escolaId]);

  // Filtrar ambientes com base na busca
  const ambientesFiltrados = useMemo(() => {
    const porPavimento = pavimentoId ? ambientes.filter(a => a.id_pavimento === pavimentoId) : [];
    if (!ambienteSearch || ambienteSearch.trim() === '') return porPavimento;
    const searchTerm = ambienteSearch.toLowerCase().trim();
    return porPavimento.filter(amb =>
      amb.id_ambiente?.toLowerCase().includes(searchTerm) ||
      amb.nome_ambiente?.toLowerCase().includes(searchTerm)
    );
  }, [ambientes, ambienteSearch, pavimentoId]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/ambiente-desenho');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchAmbientes = useCallback(async () => {
    try {
      const response = await api.get('/ambiente');
      setAmbientes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar ambientes: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchPavimentos = useCallback(async () => {
    try {
      const response = await api.get('/pavimento');
      setPavimentos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar pavimentos:', error);
    }
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar escolas:', error);
    }
  }, []);

  const fetchTiposRepresentacao = useCallback(async () => {
    try {
      const response = await api.get('/tipo-representacao');
      setTiposRepresentacao(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de representação:', error);
    }
  }, []);

  useEffect(() => {
    verificarAPI();
    fetchAmbientes();
    fetchPavimentos();
    fetchEscolas();
    fetchTiposRepresentacao();
  }, [verificarAPI, fetchAmbientes, fetchPavimentos, fetchEscolas, fetchTiposRepresentacao]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'desenho do ambiente', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/ambiente-desenho/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'desenho do ambiente', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'desenho do ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'desenho do ambiente', { id });
    try {
      const response = await api.delete(`/ambiente-desenho/${id}`);
      logarSucesso('Excluir', 'desenho do ambiente', response.data);
      setDados(currentData => currentData.filter(item => item.id_desenho_ambiente !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'desenho do ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useCallback(() => {
    const colunasBase = [
      {
        title: 'Nome da Escola',
        field: 'nome_escola',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Escola...',
        sorter: 'string',
      },
      {
        title: 'ID Desenho Ambiente',
        field: 'id_desenho_ambiente',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'number'
      },
      {
        title: 'ID Desenho',
        field: 'id_desenho',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'Tipo Representação',
        field: 'id_tipo_representacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Rep. ...',
        sorter: 'string',
        editor: 'list',
        editorParams: {
          values: tiposRepresentacao.reduce((acc, tr) => { acc[tr.id_tipo_representacao] = tr.descricao; return acc; }, {}),
          autocomplete: true,
          listOnEmpty: true,
          clearable: true,
        },
        formatter: (cell) => {
          const val = cell.getValue();
          const tr = tiposRepresentacao.find(t => t.id_tipo_representacao === val);
          return tr ? tr.descricao : val;
        }
      },
      {
        title: 'ID Ambiente',
        field: 'id_ambiente',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_desenho_ambiente, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_desenho_ambiente}"?`)) {
            excluirRegistro(data.id_desenho_ambiente);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({
        ...col,
        editor: ['id_desenho_ambiente', 'nome_escola'].includes(col.field) ? undefined : 'input'
      }))
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/ambiente-desenho');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas(),
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_desenho_ambiente'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas());
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, getColunas, inicializarTabulator]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!formData.file) throw new Error('Selecione um desenho');
      if (!escolaId) throw new Error('Selecione uma escola');
      if (!pavimentoId) throw new Error('Selecione um pavimento');
      if (!formData.id_ambiente) throw new Error('Selecione um ambiente');
      if (!formData.id_tipo_representacao) throw new Error('Tipo de representação é obrigatório');

      const data = new FormData();
      data.append('principal', formData.principal);
      data.append('id_tipo_representacao', formData.id_tipo_representacao);
      data.append('id_escola', escolaId);
      data.append('file', formData.file);

      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.id) data.append('usuario', user.id);

      const baseUrl = api.defaults.baseURL;
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${baseUrl}/uploads/ambientes/${formData.id_ambiente}/desenho`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: data
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Erro no upload');

      mostrarMensagem('success', 'Desenho enviado com sucesso!');
      setFormData(prev => ({ ...prev, file: null, principal: true, id_tipo_representacao: '' }));
      if (fileInputRef.current) fileInputRef.current.value = '';
      carregarDados(false);
    } catch (error) {
      mostrarMensagem('error', error.message || 'Erro ao enviar desenho.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setFormData(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'ambiente_desenho.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Upload de Desenhos de Ambiente</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Upload de desenhos de ambientes</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/ambiente desenho.mov" buttonText="Ver Tutorial Ambiente Desenho" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={handleUpload} className="form-inline">
          <div className="form-group">
            <label htmlFor="file">Desenho:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="file"
              id="file"
              name="file"
              accept=".pdf,.dwg,.dxf"
              onChange={handleFileChange}
              ref={fileInputRef}
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_tipo_representacao">Tipo Representação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_tipo_representacao"
              name="id_tipo_representacao"
              value={formData.id_tipo_representacao}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              required
            >
              <option value="">Selecione...</option>
              {tiposRepresentacao.map(tr => (
                <option key={tr.id_tipo_representacao} value={tr.id_tipo_representacao}>{tr.descricao}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="escola_filtro_amb_des">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="escola_filtro_amb_des"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const found = escolas.find(esc =>
                  `${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})` === e.target.value
                );
                setEscolaId(found ? found.id_escola : '');
                setPavimentoSearch('');
                setPavimentoId('');
                setAmbienteSearch('');
                setFormData(prev => ({ ...prev, id_ambiente: '' }));
              }}
              list="escolas-list-amb-des"
              placeholder="Selecione a escola primeiro..."
              disabled={loading || apiStatus !== 'connected'}
              autoComplete="off"
            />
            <datalist id="escolas-list-amb-des">
              {escolasFiltradas.map(esc => (
                <option key={esc.id_escola} value={`${esc.nome_escola} (${esc.codigo_inep || esc.id_escola})`} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="pavimento_filtro_amb_des">Pavimento:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="pavimento_filtro_amb_des"
              value={pavimentoSearch}
              onChange={(e) => {
                setPavimentoSearch(e.target.value);
                const found = pavimentosFiltrados.find(p =>
                  `${p.id_pavimento} - ${p.descr_pavimento || ''}`.trim() === e.target.value
                );
                setPavimentoId(found ? found.id_pavimento : '');
                setAmbienteSearch('');
                setFormData(prev => ({ ...prev, id_ambiente: '' }));
              }}
              list="pavimentos-list-amb-des"
              placeholder={escolaId ? 'Digite para buscar pavimento...' : 'Selecione a escola antes...'}
              disabled={loading || apiStatus !== 'connected' || !escolaId}
              autoComplete="off"
            />
            <datalist id="pavimentos-list-amb-des">
              {pavimentosFiltrados.map(p => (
                <option key={p.id_pavimento} value={`${p.id_pavimento} - ${p.descr_pavimento || ''}`.trim()} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_ambiente">Ambiente:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_ambiente"
              name="id_ambiente"
              value={ambienteSearch}
              onChange={(e) => {
                setAmbienteSearch(e.target.value);
                const found = ambientesFiltrados.find(amb =>
                  `${amb.id_ambiente} - ${amb.nome_ambiente || ''}`.trim() === e.target.value
                );
                if (found) {
                  setFormData(prev => ({ ...prev, id_ambiente: found.id_ambiente }));
                } else {
                  setFormData(prev => ({ ...prev, id_ambiente: '' }));
                }
              }}
              list="ambientes-list-desenho"
              placeholder={pavimentoId ? 'Digite para buscar ambiente...' : 'Selecione o pavimento antes...'}
              disabled={loading || apiStatus !== 'connected' || !pavimentoId}
              autoComplete="off"
            />
            <datalist id="ambientes-list-desenho">
              {ambientesFiltrados.map(item => (
                <option key={item.id_ambiente} value={`${item.id_ambiente} - ${item.nome_ambiente || ''}`.trim()} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="principal">Principal:</label>
            <input
              type="checkbox"
              id="principal"
              name="principal"
              checked={formData.principal}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Enviando...' : '⬆️ Enviar Desenho'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatórios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default AmbienteDesenhoManager;
